import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Cashpurr } from '@/components/ui/cashpurr';
import { NotificationSystem } from '@/components/ui/notification-system';
import { cn } from '@/lib/utils';
import { 
  BarChart3, 
  Target, 
  CheckSquare, 
  Book, 
  Users, 
  Settings, 
  Home,
  LogOut,
  Share2,
  Sparkles
} from 'lucide-react';

const AppSidebar: React.FC = () => {
  const location = useLocation();
  const { user, signOut } = useAuth();

  const navigation = [
    { name: 'Dashboard', href: '/app', icon: Home },
    { name: 'Idea Assessment', href: '/app/idea-validation', icon: Target },
    { name: 'Boss Mode', href: '/app/boss', icon: CheckSquare },
    { name: 'Playbooks', href: '/app/playbooks', icon: Book },
    { name: 'ROI Tracker', href: '/app/roi', icon: BarChart3 },
    { name: 'Social Tracker', href: '/app/social', icon: Share2 },
    { name: 'Prompt Library', href: '/app/prompts', icon: Sparkles },
    { name: 'Community Pods', href: '/app/pods', icon: Users },
    { name: 'Founder Journal', href: '/app/journal', icon: Book },
    { name: 'Settings', href: '/app/settings', icon: Settings },
  ];

  return (
    <div className="w-64 bg-card border-r border-border flex flex-col h-screen">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <Link to="/app" className="flex items-center space-x-2">
          <img
            src="/favicon.ico"
            alt="30 Day Startup Streak"
            className="w-8 h-8 rounded-md object-contain"
          />
          <span className="text-lg font-bold bg-gradient-primary bg-clip-text text-transparent">
            StartupStreak
          </span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <li key={item.name}>
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors duration-200',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-primary-foreground">
              {user?.email?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {user?.email}
            </p>
          </div>
          <NotificationSystem />
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={signOut}
          className="w-full justify-start text-muted-foreground hover:text-foreground"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

export default AppSidebar;